import Axios from "axios";
import router from "@/app/router";
import store from "@/app/store";
import { clearRestoreToken, readRestoreToken, saveSession } from "./session";

/**
 * Классы отказов. Облако различает их кодами и телом ответа:
 *   401            — сессия недействительна (страница входа, без полосы связи);
 *   402/403/404/422 — доменный отказ школы (текст показывает вьюха);
 *   410            — школа недоступна, запрос в школу не ушёл;
 *   503 + school_unavailable — школа недоступна, запрос в школу не ушёл;
 *   504            — школа не ответила, результат запроса неизвестен;
 *   5xx            — недоступно облако.
 */
export const KIND = {
  SESSION: "session",
  DENIED: "denied",
  DOMAIN: "domain",
  SCHOOL_DOWN: "school_down",
  CLOUD_DOWN: "cloud_down",
  OFFLINE: "offline",
  UNKNOWN: "unknown"
};

const MESSAGES = {
  [KIND.SCHOOL_DOWN]: "Нет связи со школьным сервером",
  [KIND.CLOUD_DOWN]: "Сервис временно недоступен",
  [KIND.OFFLINE]: "Нет соединения с сетью",
  [KIND.SESSION]: "Сессия недействительна",
  [KIND.DENIED]: "Недостаточно прав",
  [KIND.DOMAIN]: "Запрос отклонён",
  [KIND.UNKNOWN]: "Неизвестная ошибка"
};

const CONNECTION_KINDS = [KIND.SCHOOL_DOWN, KIND.CLOUD_DOWN, KIND.OFFLINE];
const RETRY_DELAY = 700;
const CLIENT_TIMEOUT_CODE = "ECONNABORTED";

function isDeviceOffline() {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

function responsePayload(response) {
  const data = response && response.data;
  return data && typeof data === "object" ? data : {};
}

/** Единый разбор ошибки запроса: чем считать отказ и что показать. */
export function describeError(error) {
  const response = error && error.response;
  const status = response ? response.status : null;

  if (!response) {
    if (isDeviceOffline()) {
      return {
        kind: KIND.OFFLINE,
        reason: "offline",
        status,
        message: MESSAGES[KIND.OFFLINE]
      };
    }

    if (error && error.code === CLIENT_TIMEOUT_CODE) {
      return {
        kind: KIND.CLOUD_DOWN,
        reason: "timeout",
        status,
        message: MESSAGES[KIND.CLOUD_DOWN]
      };
    }

    return {
      kind: KIND.CLOUD_DOWN,
      reason: "network",
      status,
      message: MESSAGES[KIND.CLOUD_DOWN]
    };
  }

  const payload = responsePayload(response);
  const reason = payload.reason || `http_${status}`;

  // Отказ школы — это не потеря сессии: облако помечает такие ответы телом
  // `school_unavailable` (в том числе когда школа сама ответила 401 на RPC).
  // Иначе SPA уводил учителя на форму входа при живом cookie.
  if (payload.error === "school_unavailable") {
    return {
      kind: KIND.SCHOOL_DOWN,
      reason: payload.reason || "no_ping",
      status,
      message: payload.message || MESSAGES[KIND.SCHOOL_DOWN]
    };
  }

  if (status === 401)
    return {
      kind: KIND.SESSION,
      reason,
      status,
      message: MESSAGES[KIND.SESSION]
    };
  if (status === 403)
    return {
      kind: KIND.DENIED,
      reason,
      status,
      message: MESSAGES[KIND.DENIED]
    };

  if (status === 410) {
    return {
      kind: KIND.SCHOOL_DOWN,
      reason: "no_ping",
      status,
      message: MESSAGES[KIND.SCHOOL_DOWN]
    };
  }

  if (status === 504) {
    return {
      kind: KIND.SCHOOL_DOWN,
      reason: "timeout",
      status,
      message: MESSAGES[KIND.SCHOOL_DOWN]
    };
  }

  if (status >= 500) {
    return {
      kind: KIND.CLOUD_DOWN,
      reason,
      status,
      message: MESSAGES[KIND.CLOUD_DOWN]
    };
  }

  if (status >= 400) {
    return {
      kind: KIND.DOMAIN,
      reason,
      status,
      message: MESSAGES[KIND.DOMAIN]
    };
  }

  return {
    kind: KIND.UNKNOWN,
    reason,
    status,
    message: MESSAGES[KIND.UNKNOWN]
  };
}

function reportFailure(error) {
  const info = describeError(error);
  const config = error.config || {};
  const url = String(config.url || "");

  if (info.kind === KIND.SESSION) {
    if (!url.endsWith("/login") && router.currentRoute.name !== "login") {
      router.push({ name: "login" }).catch(() => {});
    }

    return info;
  }

  if (CONNECTION_KINDS.includes(info.kind)) {
    store.dispatch("connection/failure", {
      kind: info.kind,
      reason: info.reason
    });
  }

  return info;
}

function reportSuccess() {
  store.dispatch("connection/success");
}

/**
 * Повторяем только идемпотентные запросы и только когда облако успело
 * ответить: иначе к ожиданию прибавляется второй таймаут. Запросы оформления
 * заявки (POST) не повторяются никогда — см. Home.vue и Recovery.vue.
 */
function shouldRetry(error, info) {
  const config = error.config || {};
  const method = String(config.method || "get").toLowerCase();

  return (
    method === "get" &&
    !config.__retried &&
    Boolean(error.response) &&
    (info.kind === KIND.SCHOOL_DOWN || info.kind === KIND.CLOUD_DOWN)
  );
}

function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Запросы, не зависящие от школы: их успех не значит, что школа на связи,
 * поэтому состояние «нет связи со школой» они не снимают.
 */
const CLOUD_LOCAL_PATHS = ["/current-user", "/logout"];

function matchesPath(config, paths) {
  const url = String((config && config.url) || "").split("?")[0];
  return paths.some(path => url.startsWith(path));
}

/**
 * Пути, для которых восстановление не запускаем: сами ручки авторизации (иначе
 * `401` на `/auth/restore` зациклит попытки) и выход.
 */
const NO_RESTORE_PATHS = ["/auth", "/logout"];

/** `401` от облака на данных кабинета — единственный повод для восстановления. */
function shouldRestore(config, error) {
  const response = error && error.response;

  if (!response || response.status !== 401) return false;
  if (config.__restored || config.__restoreAttempt) return false;
  if (matchesPath(config, NO_RESTORE_PATHS)) return false;

  return Boolean(readRestoreToken());
}

/**
 * Восстановление внешней сессии по restore-токену. Одновременные запросы делят
 * одну попытку: `restoreInFlight` не даёт отправить десяток `/auth/restore`
 * параллельно, когда cookie потеряна у всей страницы.
 *
 * Отказ (токен просрочен, строка сессии закрыта) не ошибка сам по себе: токен
 * забываем, дальше SPA покажет форму входа, как раньше.
 */
let restoreInFlight = null;

function restoreSession() {
  if (restoreInFlight) return restoreInFlight;

  const restoreToken = readRestoreToken();
  if (!restoreToken) return Promise.resolve(false);

  restoreInFlight = (async () => {
    try {
      const { data } = await authorization.restore(restoreToken);
      saveSession(data);
      store.commit("setExplicitLogout", false);
      return true;
    } catch (e) {
      clearRestoreToken();
      return false;
    } finally {
      restoreInFlight = null;
    }
  })();

  return restoreInFlight;
}

function attachInterceptors(instance) {
  instance.interceptors.response.use(
    response => {
      const config = response.config || {};

      if (!config.__silent && !matchesPath(config, CLOUD_LOCAL_PATHS)) {
        reportSuccess();
      }

      return response;
    },
    async error => {
      const config = error.config || {};

      if (config.__silent) throw error;

      // Cookie сессии потеряна (webview мессенджера, очистка данных браузера,
      // другой профиль), но строка сессии в облаке может быть ещё жива:
      // восстанавливаем её restore-токеном и повторяем исходный запрос один раз.
      if (shouldRestore(config, error)) {
        const restored = await restoreSession();

        if (restored) {
          config.__restored = true;
          return instance.request(config);
        }
      }

      const info = reportFailure(error);

      if (shouldRetry(error, info)) {
        config.__retried = true;
        await delay(RETRY_DELAY);
        return instance.request(config);
      }

      throw error;
    }
  );
}

const instance = Axios.create({
  baseURL: `${location.origin}/api/v1/teacher`,
  timeout: 12000
});

const auth = Axios.create({
  baseURL: `${location.origin}/api/v1`,
  timeout: 12000
});

attachInterceptors(instance);
attachInterceptors(auth);

const authorization = {
  login: (login, password) =>
    auth.post(
      `/auth?login=${login}&pwd=${password}`,
      {},
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8"
        }
      }
    ),
  loginWithToken: token =>
    auth.post(
      `/auth?token=${token}`,
      {},
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8"
        }
      }
    ),
  /**
   * Восстановление сессии по токену, сохранённому при входе. Запрос помечен
   * `__silent`/`__restoreAttempt`: его собственный `401` не должен ни уводить на
   * форму входа, ни запускать восстановление повторно.
   */
  restore: restoreToken =>
    auth.post(
      `/auth/restore`,
      { restore_token: restoreToken },
      { __silent: true, __restoreAttempt: true }
    ),
  logout: () => auth.get(`/logout`),
  currentUser: () => auth.get(`/current-user`)
};

const classes = {
  getClasses: () => instance.get("/class"),
  getClassLearners: (classId, date) =>
    instance.get(`/class/${classId}/learners${date ? `?date=${date}` : ""}`)
};

const menu = {
  getMenu: () => instance.get("/menu")
};

const requests = {
  closeRequest: (classId, body, date, recovery = false) => {
    const params = [];
    if (date) params.push(`date=${date}`);
    if (recovery) params.push("recovery=true");
    const query = params.length ? `?${params.join("&")}` : "";
    return instance.post(`/class/${classId}/request/issue${query}`, body);
  }
};

const other = {
  time: () => instance.get("/time"),
  getToken: () => instance.get("/token"),
  // Окно доступных дат восстановления (min/max/available/todayExcluded) и today школы.
  requestDateRules: () => instance.get("/request-date-rules")
};

const monitoring = {
  getClasses: () => instance.get(`/event/classes`),
  getLearners: (id, date) =>
    instance.get(`/event/class/${id}/learners?date=${date}`)
};

/**
 * Проба связи. Отвечает облако, школа при этом не опрашивается: в ответе
 * `state` = "online" | "offline" и `ping`. Флаг __silent отключает обычный
 * учёт — состояние выставляет сама полоса связи.
 */
const connection = {
  probe: () => auth.get("/teacher/school", { timeout: 8000, __silent: true })
};

export default {
  ...other,
  authorization,
  classes,
  menu,
  requests,
  monitoring,
  connection,
  describeError
};
